/**
 * Локальная версия Map с правильной поддержкой темной темы.
 * Обходит проблему кеширования в packages/blocks/map/Map.hooks.ts
 */
import {Loader} from '@yandex-delivery/blocks'
import {MapsContextProvider, useMapContextValue} from '@yandex-delivery/blocks/map/context'
import {MapMargin} from '@yandex-delivery/blocks/map/MapMargin'
import type {MapProps as MapPropsBase, MapRef} from '@yandex-delivery/blocks/map/types'
import clsx from 'clsx'
import React, {useState} from 'react'
import type {Brand} from 'src/shared/types'

import {useMapTheme} from './useMapTheme'

type MapProps = MapPropsBase &
  React.PropsWithChildren<{
    brand?: Brand
    errorMessage?: string
    lang: string
    renderFallback?(): React.ReactElement
    theme?: 'light' | 'dark'
  }>

type MapProvidedProps = MapProps & {
  mapRef?: MapRef
  setMapRef: (ref: MapRef | null | undefined) => void
}

const defaultRenderFallback = () => <Loader />

const MapProvided: React.FC<MapProvidedProps> = ({
  children,
  renderFallback = defaultRenderFallback,
  theme = 'light',
  setMapRef,
  className,
  ...props
}) => {
  const mapContext = useMapContextValue()

  // КЛЮЧЕВОЕ ОТЛИЧИЕ: используем локальный хук вместо useCustomization из packages/blocks
  const customization = useMapTheme(theme)

  if (!mapContext || !customization) {
    return renderFallback()
  }

  const {
    components: {YMap, YMapDefaultFeaturesLayer, YMapDefaultSchemeLayer},
  } = mapContext

  return (
    <YMap className={clsx('map-root', className)} copyrights={false} mode="vector" ref={setMapRef} {...props}>
      <MapMargin margin={props.margin} />
      <YMapDefaultSchemeLayer customization={customization} />
      <YMapDefaultFeaturesLayer />
      {children}
    </YMap>
  )
}

export const MapWithTheme: React.FC<MapProps> = ({brand = 'yandex', errorMessage, ...props}) => {
  const [mapRef, setMapRef] = useState<MapRef | null>()
  return (
    <MapsContextProvider
      brand={brand}
      errorMessage={errorMessage}
      lang={props.lang}
      mapRef={mapRef}
      setMapRef={setMapRef}
    >
      <MapProvided setMapRef={setMapRef} {...props} />
    </MapsContextProvider>
  )
}
